/**
 * The rules of a form's question list, apart from the screen that draws
 * it (Settings → Questions, ADR-0022).
 *
 * The screen holds the list as sentences — the words the Company typed,
 * each with the id of the block the server wrote for it and the
 * conditions the Company put on it — and every edit to it is a function
 * here from one list to the next. None of them saves: which edits commit
 * at once and which wait for the line to be left is the screen's wiring,
 * and the write itself is `useQuestionListSave`'s. What is left here is
 * what a list means: what is sent, what counts toward the cap, what a
 * question may wait on, and how the list the server answered with is
 * taken back onto the one on screen.
 *
 * One flat list (ADR-0032): a question may wait on the answer to any
 * yes-or-no or pick-one question above it, or on whether the event is
 * indoors or outdoors, and on several of those at once. It was a tree
 * of sub-questions one level deep until then, which could not say
 * "ask this if X and Y".
 */
import type { SelectOption } from "../../components/ui/Select";
import type {
  InquiryQuestion,
  InquiryQuestionCondition,
  InquiryQuestionSentence,
} from "../../lib/settings-api";
import { ApiError } from "../../../../shared/api-client";

/**
 * The most sentences one form may ask — the server's
 * `profile.MaxQuestions`, counted as the Company counts them: every
 * line on the screen.
 */
export const MAX_QUESTIONS = 20;

/** The most conditions one question may wait on (`profile.MaxConditions`). */
export const MAX_CONDITIONS = 4;

/**
 * The one core answer a question may wait on: whether the event is
 * indoors or outdoors (`profile.ConditionField`), with the answers the
 * form offers for it.
 */
export const CORE_CONDITION_FIELD = "indoorOutdoor";
const CORE_CONDITION_LABEL = "Indoors or outdoors?";
const CORE_CONDITION_ANSWERS = ["Indoor", "Outdoor"];

/** One question as the screen holds it: its words, its id, its conditions. */
export function sentenceOf(q: InquiryQuestion): InquiryQuestionSentence {
  const when = (q.when ?? []).map(({ field, equals }) => ({ field, equals }));
  return when.length > 0
    ? { id: q.id, label: q.label, when }
    : { id: q.id, label: q.label };
}

/** The sentences as the screen holds them: what was loaded, then edited. */
export function sentencesOf(
  questions: InquiryQuestion[],
): InquiryQuestionSentence[] {
  return questions.map(sentenceOf);
}

/**
 * A write the server refused, as against one that could not be made. A
 * refusal is the server saying what it holds is what stays, so the
 * screen goes back to that; a write that failed on the way — the model
 * down, the network gone — keeps the list, so "try again" is leaving
 * the line again rather than typing it again.
 */
export function isRefusal(err: unknown): boolean {
  return err instanceof ApiError && err.status >= 400 && err.status < 500;
}

/**
 * The list as it is sent: a blank line is a half-finished edit, not a
 * question, and a condition with nothing picked yet is not sent.
 */
export function toSend(
  draft: InquiryQuestionSentence[],
): InquiryQuestionSentence[] {
  return draft
    .filter((s) => s.label.trim() !== "")
    .map((s) => {
      const when = (s.when ?? []).filter((c) => c.field !== "" && c.equals !== "");
      const line: InquiryQuestionSentence =
        s.id === undefined ? { label: s.label } : { id: s.id, label: s.label };
      return when.length > 0 ? { ...line, when } : line;
    });
}

/** Same ids, same words and same conditions in the same order: nothing to update. */
export function sameSentences(
  a: InquiryQuestionSentence[],
  b: InquiryQuestionSentence[],
): boolean {
  const sameCondition = (x: InquiryQuestionCondition, y: InquiryQuestionCondition) =>
    x.field === y.field && x.equals === y.equals;
  return (
    a.length === b.length &&
    a.every((s, i) => {
      const aw = s.when ?? [];
      const bw = b[i].when ?? [];
      return (
        (s.id ?? "") === (b[i].id ?? "") &&
        s.label.trim() === b[i].label.trim() &&
        aw.length === bw.length &&
        aw.every((c, j) => sameCondition(c, bw[j]))
      );
    })
  );
}

/** How many lines count toward the cap: every sentence on the screen. */
export function lineCount(draft: InquiryQuestionSentence[]): number {
  return draft.length;
}

/**
 * The answers a condition can name on a question, from the question the
 * server wrote for its line: yes or no, or one of its options. Nothing
 * for any other kind — a condition waits only on those two.
 */
export function answersOf(source: InquiryQuestion | undefined): SelectOption[] {
  if (source?.type === "checkbox") {
    return [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ];
  }
  if (source?.type === "select") {
    return (source.options ?? []).map((o) => ({ value: o, label: o }));
  }
  return [];
}

/**
 * What one line may wait on: whether the event is indoors or outdoors,
 * and every yes-or-no or pick-one question above it that the server has
 * written — a line not yet saved has no kind, and a line below it is
 * not asked yet.
 */
export function sourcesOf(
  draft: InquiryQuestionSentence[],
  index: number,
  questions: InquiryQuestion[],
): SelectOption[] {
  const sources: SelectOption[] = [
    { value: CORE_CONDITION_FIELD, label: CORE_CONDITION_LABEL },
  ];
  for (const line of draft.slice(0, index)) {
    if (line.id === undefined) continue;
    const q = questions.find((x) => x.id === line.id);
    if (answersOf(q).length === 0) continue;
    sources.push({ value: line.id, label: line.label.trim() || q!.label });
  }
  return sources;
}

/** The answers a condition may pick from, given what it waits on. */
export function answersFor(
  field: string,
  questions: InquiryQuestion[],
): SelectOption[] {
  if (field === CORE_CONDITION_FIELD) {
    return CORE_CONDITION_ANSWERS.map((a) => ({ value: a, label: a }));
  }
  return answersOf(questions.find((q) => q.id === field));
}

/**
 * The options a condition can pick from, and the one it holds if that
 * is no longer offered — shown as it stands rather than dropped.
 */
export function withCurrent(
  options: SelectOption[],
  current: string,
): SelectOption[] {
  if (current === "" || options.some((a) => a.value === current)) {
    return options;
  }
  return [...options, { value: current, label: current }];
}

/**
 * Which question means dry hire, and the answer that does: the first
 * question the generate step marked, as the server counts it
 * (`profile.DryHireQuestion`). A question waiting on another never
 * means dry hire.
 */
export function dryHireOf(
  questions: InquiryQuestion[],
): { id: string; answer: string } | null {
  const q = questions.find(
    (x) =>
      (x.dryHireAnswer ?? "") !== "" &&
      !(x.when ?? []).some((c) => c.field !== CORE_CONDITION_FIELD),
  );
  if (!q?.dryHireAnswer) return null;
  const a = q.dryHireAnswer;
  // A checkbox's answers are stored as "yes" and "no" and read as the
  // condition picker shows them; a choice's option is its own words.
  const answer =
    q.type === "checkbox" ? (a === "yes" ? "Yes" : a === "no" ? "No" : a) : a;
  return { id: q.id, answer };
}

/**
 * The list the server wrote, adopted by the screen: every line that was
 * sent takes the question the server made of it — its id above all,
 * which is new wherever a reword came back another kind of question, and
 * a line left on the old one is a line the next write sends as new and
 * has written again. The server answers in the order it was sent, so a
 * sent line is paired with its question by position.
 *
 * A line edited while the write was out keeps its words for the next
 * write to send, under the id the server gave the line it was.
 */
export function adopt(
  draft: InquiryQuestionSentence[],
  sent: InquiryQuestionSentence[],
  questions: InquiryQuestion[],
): InquiryQuestionSentence[] {
  const lineFor = new Map<InquiryQuestionSentence, InquiryQuestion>();
  const byOldId = new Map<string, InquiryQuestion>();
  let k = 0;
  for (const line of sent) {
    if (line.label.trim() === "") continue;
    const q = questions[k++];
    if (!q) break;
    lineFor.set(line, q);
    if (line.id) byOldId.set(line.id, q);
  }
  // A condition names a line by its id, so a line whose id the server
  // changed is followed by every condition that named it.
  const renamed = new Map<string, string>();
  for (const [old, q] of byOldId) if (old !== q.id) renamed.set(old, q.id);
  const followed = (line: InquiryQuestionSentence): InquiryQuestionSentence => {
    if (!line.when?.some((c) => renamed.has(c.field))) return line;
    return {
      ...line,
      when: line.when.map((c) =>
        renamed.has(c.field) ? { ...c, field: renamed.get(c.field)! } : c,
      ),
    };
  };
  return draft.map((line) => {
    const q = lineFor.get(line);
    if (q) return sentenceOf(q);
    const moved = line.id ? byOldId.get(line.id) : undefined;
    return followed(moved ? { ...line, id: moved.id } : line);
  });
}

// ─── Edits ──────────────────────────────────────────────────────
//
// Each takes the list and returns the next one, and never touches a
// line it was not asked about: `adopt` pairs lines by identity, so a
// line an edit leaves alone must stay the same object.

type Draft = InquiryQuestionSentence[];

/** Swaps one row of a list to another place, or leaves it where it is. */
function moved<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row);
  return next;
}

/** Rewrites one line's conditions, leaving every other line as it is. */
function withWhen(
  draft: Draft,
  index: number,
  change: (when: InquiryQuestionCondition[]) => InquiryQuestionCondition[],
): Draft {
  return draft.map((s, i) => {
    if (i !== index) return s;
    const when = change(s.when ?? []);
    if (when.length === 0) {
      const { when: _dropped, ...rest } = s;
      return rest;
    }
    return { ...s, when };
  });
}

/** A new blank line at the end, unless the list is full. */
export function add(draft: Draft): Draft {
  if (lineCount(draft) >= MAX_QUESTIONS) return draft;
  return [...draft, { label: "" }];
}

/** A new condition on one line, on the first thing it can wait on. */
export function addCondition(
  draft: Draft,
  index: number,
  field: string,
  equals: string,
): Draft {
  if ((draft[index]?.when?.length ?? 0) >= MAX_CONDITIONS) return draft;
  return withWhen(draft, index, (when) => [...when, { field, equals }]);
}

/** A condition moved to wait on another line, its answer the first that line offers. */
export function setConditionField(
  draft: Draft,
  index: number,
  condition: number,
  field: string,
  equals: string,
): Draft {
  return withWhen(draft, index, (when) =>
    when.map((c, j) => (j === condition ? { field, equals } : c)),
  );
}

export function setConditionAnswer(
  draft: Draft,
  index: number,
  condition: number,
  equals: string,
): Draft {
  return withWhen(draft, index, (when) =>
    when.map((c, j) => (j === condition ? { ...c, equals } : c)),
  );
}

export function removeCondition(
  draft: Draft,
  index: number,
  condition: number,
): Draft {
  return withWhen(draft, index, (when) => when.filter((_, j) => j !== condition));
}

/** A question moved, its conditions with it. */
export function move(draft: Draft, from: number, to: number): Draft {
  return moved(draft, from, to);
}

/** A question removed, and every condition on it. */
export function remove(draft: Draft, index: number): Draft {
  return draft.filter((_, i) => i !== index);
}

export function edit(draft: Draft, index: number, label: string): Draft {
  return draft.map((s, i) => (i === index ? { ...s, label } : s));
}

/**
 * The list once a line is left. A saved line left blank is the remove
 * button pressed: gone, and written as a removal by the commit that
 * follows. A new line left blank is a line not written yet, and stays
 * where it is: the cursor leaves it every time `+` is pressed again.
 * Anything else is the list as it was.
 */
export function leave(draft: Draft, index: number): Draft {
  const line = draft[index];
  if (!line || line.label.trim() !== "" || line.id === undefined) return draft;
  return remove(draft, index);
}
